import { addDays, isSameDay, startOfDay } from 'date-fns';
import i18n from 'i18next';
import { Baby, Car, Hospital, ShoppingBag, Utensils } from 'lucide-react';
import type { Task } from '@/types/api';
import { msg, resolveMsg, type AnyTFunction, type Msg } from '@/i18n/msg';
import { labelFor } from '@/i18n/labels';
import type { DashboardMode } from '@/lib/dashboard-mode';
import { isHiddenFromBereavedFamily } from '@/lib/bereavement-tone';

// ── The memorial gate on the parent's own task surfaces (#693) ─────────────
//
// #644 built memorial mode and wired it to exactly one page. `/tasks`,
// `/calendar` and the dashboard's This-week and Recent-activity sections all
// read the same `GET /api/tasks` payload and none of them knew the family's
// stage, so a bereaved parent kept being asked to pick up their child's
// prescription — every day, on three surfaces, until a staff member noticed.
//
// Every parent surface that renders task rows now passes its list through
// this one function, so extending the gate is not a per-page judgment call.
// The mode comes from `resolveDashboardMode` (lib/dashboard-mode.ts) — the
// same parent-side mapping DashboardPage has always used, reached through
// `useDashboardMode` so a new surface cannot forget the query.
//
// The filter is silent by construction: it returns a shorter array. There is
// no tombstone row and no "closed because" note, because a tombstone is the
// same sentence with an apology attached — the family should not be asked to
// witness the bookkeeping.

/**
 * The tasks a parent may see, given their family's dashboard mode.
 *
 * Two removals, and they are not the same removal:
 *
 *  - `archived` items go in every mode. That status only exists as the
 *    bereavement reconcile resolution, but the rule is about the status, not
 *    the mode: an archived item is staff history and never a parent row.
 *  - Open treatment-era items go in memorial mode only, unless a TRFer has
 *    explicitly kept them (see `isHiddenFromBereavedFamily`).
 *
 * Non-treatment items are untouched. Bereaved families keep real open
 * business with TRF — equipment returns, grant paperwork — and the #644 memo
 * commits to that in as many words: "Bereaved families still have practical
 * business with TRF, and the page should not pretend otherwise." Blanking
 * the list would be a second, quieter version of the same mistake.
 */
export function visibleParentTasks(tasks: Task[], mode: DashboardMode): Task[] {
  if (mode !== 'memorial') return tasks.filter((t) => t.status !== 'archived');
  return tasks.filter((t) => !isHiddenFromBereavedFamily(t));
}


// ── Humanized status (UI layer) ────────────────────────────────────────────
//
// Backend stores `open / claimed / in_progress / completed / cancelled`.
// The UI groups these into editorial buckets so families don't have to think
// about workflow internals.

export type UiStatus = 'all' | 'open' | 'in_motion' | 'completed';

// The labels match the list's own section headings (To do / In progress /
// Recently done) so the popover's "Show" group and the groups it narrows
// use one vocabulary. "Needs a helper" outlived the helper removal (P2b).
//
// Key maps, resolved at render with `labelFor` (#2109 R3-B4). No staff file
// imports any map in this module, so no English compatibility getter is kept.
export const UI_STATUS_LABEL_KEY = {
  all: 'tasks:status.all',
  open: 'tasks:status.open',
  in_motion: 'tasks:status.in_motion',
  completed: 'tasks:status.completed',
} as const satisfies Record<UiStatus, string>;

export const ALL_UI_STATUSES: readonly UiStatus[] = ['all', 'open', 'in_motion', 'completed'];

export const UI_STATUS_ORDER: UiStatus[] = ['open', 'in_motion', 'completed'];

export function matchesUiStatus(task: Task, ui: UiStatus): boolean {
  if (ui === 'all') return task.status !== 'cancelled';
  if (ui === 'open') return task.status === 'open';
  if (ui === 'in_motion') return task.status === 'claimed' || task.status === 'in_progress';
  if (ui === 'completed') return task.status === 'completed';
  return false;
}

export function uiStatusForTask(task: Task): UiStatus | null {
  if (task.status === 'open') return 'open';
  if (task.status === 'claimed' || task.status === 'in_progress') return 'in_motion';
  if (task.status === 'completed') return 'completed';
  return null;
}

// ── Humanized urgency ──────────────────────────────────────────────────────

export type UiUrgency = 'urgent' | 'soon' | 'flexible';

export const UI_URGENCY_LABEL_KEY = {
  urgent: 'tasks:urgency.urgent',
  soon: 'tasks:urgency.soon',
  flexible: 'tasks:urgency.flexible',
} as const satisfies Record<UiUrgency, string>;

export const UI_URGENCY_ORDER: UiUrgency[] = ['urgent', 'soon', 'flexible'];

export function uiUrgencyForTask(task: Task): UiUrgency {
  if (task.urgency === 'critical' || task.urgency === 'high') return 'urgent';
  if (task.urgency === 'low') return 'flexible';
  return 'soon';
}

export function matchesUiUrgency(task: Task, ui: UiUrgency | undefined): boolean {
  if (!ui) return true;
  return uiUrgencyForTask(task) === ui;
}

// ── Editorial color tokens ─────────────────────────────────────────────────
//
// Urgency lives on the completion circle's ring (the Todoist idiom, 2026-09-10
// list rewrite): red is urgent, amber is soon, the default hairline is
// flexible. The old left rail and the URGENT / SOON / FLEXIBLE pill said the
// same thing twice on a card that no longer exists.

export const URGENCY_RING: Record<UiUrgency, string> = {
  urgent:   'border-status-danger-fg',
  soon:     'border-status-warn-fg',
  flexible: 'border-stevens-ink-3/60',
};

// ── Categories ─────────────────────────────────────────────────────────────

export const CATEGORY_LABEL_KEY = {
  meal: 'tasks:category.meal',
  ride: 'tasks:category.ride',
  childcare: 'tasks:category.childcare',
  errand: 'tasks:category.errand',
  hospital_stay: 'tasks:category.hospital_stay',
  other: 'tasks:category.other',
} as const satisfies Record<Task['category'], string>;

export const ALL_TASK_CATEGORIES: readonly Task['category'][] = ['meal', 'ride', 'childcare', 'errand', 'hospital_stay', 'other'];

/** The category's label in the reader's language; an unknown category renders as stored. */
export function categoryLabel(category: string | null | undefined, t: AnyTFunction): string {
  return labelFor(CATEGORY_LABEL_KEY as Record<string, (typeof CATEGORY_LABEL_KEY)[Task['category']]>, category, t);
}

export const CATEGORY_ORDER: string[] = ['meal', 'ride', 'childcare', 'errand', 'hospital_stay'];

// ── Filter state and search (declutter 2026-09-10) ─────────────────────────
//
// The chip rows above the list became the toolbar's filter popover, and the
// state they edited moved here from `components/tasks/TaskFilters.tsx` so the
// popover, the filter dot and the empty state's "clear" all read one
// definition of "off its default".

export interface TaskFilterState {
  status: UiStatus;
  category?: string;
  urgency?: UiUrgency;
  /**
   * When true, recurring tasks are rendered flat (one card per occurrence)
   * instead of collapsed into a series card with an expand affordance.
   * Default is `false` so the list stays calm even with daily/weekly tasks.
   */
  showEveryOccurrence: boolean;
}

export const DEFAULT_TASK_FILTERS: TaskFilterState = { status: 'all', showEveryOccurrence: false };

/** True when any field is off its default — drives the toolbar's filter dot. */
export function isTaskFiltered(f: TaskFilterState): boolean {
  return (
    f.status !== DEFAULT_TASK_FILTERS.status ||
    f.category !== undefined ||
    f.urgency !== undefined ||
    f.showEveryOccurrence !== DEFAULT_TASK_FILTERS.showEveryOccurrence
  );
}

/**
 * Client-side search over the loaded list: title, description and the
 * category's human label ("Ride" finds a `ride` task). Trimmed and
 * case-insensitive; an empty query matches everything, so closing the
 * search field never leaves a list filtered invisibly.
 *
 * The label is the one the reader sees (#2109 R3-B4): `t` defaults to the
 * active language, so "Viaje" finds a ride task for a Spanish reader.
 */
export function taskMatchesSearch(task: Task, q: string, t: AnyTFunction = i18n.t): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const hay = [task.title, task.description, categoryLabel(task.category, t)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(needle);
}

// Icon mapping for category chips/badges across TaskCard, the request modal,
// and any future surface that renders task categories. Was duplicated in two places
// before — single source of truth here.
export const CATEGORY_ICON: Record<string, typeof Utensils> = {
  meal: Utensils,
  ride: Car,
  childcare: Baby,
  errand: ShoppingBag,
  hospital_stay: Hospital,
};

// ── All-day tasks (#2410, D-2410) ──────────────────────────────────────────
//
// A task saved with a date and no time is all-day: `date_time` holds the
// family's LOCAL start of that day and `all_day` is true. Rows written before
// the flag carry none and read as timed. An all-day task is due at the END of
// its day — it is never overdue on its own day — and it leads that day.

/** True for a dated task saved without a time. */
export function isAllDayTask(task: Pick<Task, 'all_day' | 'date_time'>): boolean {
  return task.all_day === true && !!task.date_time;
}

/**
 * When the task is due, in epoch ms: its time, or the last millisecond of its
 * local day when it is all-day. An undated or unparseable task is never due.
 */
export function taskDueAt(task: Pick<Task, 'all_day' | 'date_time'>): number {
  const at = task.date_time ? new Date(task.date_time).getTime() : Number.NaN;
  if (!Number.isFinite(at)) return Number.POSITIVE_INFINITY;
  if (!isAllDayTask(task)) return at;
  return addDays(startOfDay(at), 1).getTime() - 1;
}

// ── Recurring series collapsing ────────────────────────────────────────────
//
// Recurring tasks are stored as N separate documents tied by `recurrence_id`.
// In the dashboard we collapse them into a single card per series so the list
// doesn't flood with identical boxes — picking the most actionable occurrence
// as the representative, and surfacing the rest behind an expand affordance.

export interface TaskSeries {
  /** Stable id used by callers as a React key + for expand state. */
  key: string;
  /** The single Task chosen to drive the visible card. */
  representative: Task;
  /**
   * All members of the series matching the active filters, sorted by
   * scheduled time ascending. For a non-recurring task this is a 1-element
   * array equal to [representative]; collapsing code can treat it uniformly.
   */
  occurrences: Task[];
  /** True iff `occurrences.length > 1` and the tasks share a `recurrence_id`. */
  isSeries: boolean;
}

/**
 * Pick the most actionable occurrence to display for a series. Priority:
 *   1. Earliest open occurrence (status === 'open').
 *   2. Earliest in-motion occurrence (claimed / in_progress).
 *   3. Earliest upcoming occurrence regardless of status.
 *   4. Most recent completed occurrence.
 *   5. The first task in the list (fallback).
 */
function pickRepresentative(tasks: Task[]): Task {
  const now = Date.now();
  const byTime = (a: Task, b: Task) => {
    const at = a.date_time ? new Date(a.date_time).getTime() : Number.POSITIVE_INFINITY;
    const bt = b.date_time ? new Date(b.date_time).getTime() : Number.POSITIVE_INFINITY;
    return at - bt;
  };

  const open = tasks.filter((t) => t.status === 'open').sort(byTime);
  if (open.length > 0) {
    // "Upcoming" reads the due moment (#2410): today's all-day occurrence is
    // still upcoming at 3 PM, though its stored instant was midnight.
    const upcoming = open.find((t) => !t.date_time || taskDueAt(t) >= now);
    return upcoming ?? open[0];
  }

  const inMotion = tasks
    .filter((t) => t.status === 'claimed' || t.status === 'in_progress')
    .sort(byTime);
  if (inMotion.length > 0) return inMotion[0];

  const upcoming = tasks
    .filter((t) => t.date_time && taskDueAt(t) >= now)
    .sort(byTime);
  if (upcoming.length > 0) return upcoming[0];

  const completed = tasks
    .filter((t) => t.status === 'completed')
    .sort((a, b) => -byTime(a, b));
  if (completed.length > 0) return completed[0];

  return tasks[0];
}

/**
 * Group tasks by `recurrence_id`, picking a representative per series.
 * Tasks without a `recurrence_id` become singleton series so callers can
 * render the result uniformly.
 */
export function groupTasksBySeries(tasks: Task[]): TaskSeries[] {
  const seriesByKey = new Map<string, Task[]>();
  const singletons: TaskSeries[] = [];

  for (const t of tasks) {
    if (!t.recurrence_id) {
      singletons.push({ key: t._id, representative: t, occurrences: [t], isSeries: false });
      continue;
    }
    const bucket = seriesByKey.get(t.recurrence_id);
    if (bucket) bucket.push(t);
    else seriesByKey.set(t.recurrence_id, [t]);
  }

  const grouped: TaskSeries[] = [];
  for (const [recurrenceId, members] of seriesByKey) {
    members.sort((a, b) => {
      const at = a.date_time ? new Date(a.date_time).getTime() : Number.POSITIVE_INFINITY;
      const bt = b.date_time ? new Date(b.date_time).getTime() : Number.POSITIVE_INFINITY;
      return at - bt;
    });
    if (members.length === 1) {
      grouped.push({ key: members[0]._id, representative: members[0], occurrences: members, isSeries: false });
    } else {
      grouped.push({
        key: `series:${recurrenceId}`,
        representative: pickRepresentative(members),
        occurrences: members,
        isSeries: true,
      });
    }
  }

  return [...grouped, ...singletons];
}

type Frequency = NonNullable<Task['recurrence']>['frequency'];

const FREQUENCY_ADVERB_KEY = {
  daily: 'tasks:recurrence.daily',
  weekly: 'tasks:recurrence.weekly',
  monthly: 'tasks:recurrence.monthly',
} as const satisfies Record<Frequency, string>;

/** "Every N <unit>" — one count key per unit, so each language pluralises its own noun (#2109 R3-B3). */
const EVERY_N_KEY = {
  daily: 'tasks:recurrence.everyDays',
  weekly: 'tasks:recurrence.everyWeeks',
  monthly: 'tasks:recurrence.everyMonths',
} as const satisfies Record<Frequency, string>;

/** A descriptor for "Daily", "Every 2 weeks", "Monthly" — resolved at render. */
export function formatRecurrenceLabel(recurrence: Task['recurrence']): Msg {
  if (!recurrence) return msg('tasks:recurrence.recurring');
  const interval = recurrence.interval && recurrence.interval > 1 ? recurrence.interval : 1;
  if (interval === 1) return msg(FREQUENCY_ADVERB_KEY[recurrence.frequency]);
  return msg(EVERY_N_KEY[recurrence.frequency], { count: interval });
}

// ── Auto-staged tasks (#2109 R3-B14) ───────────────────────────────────────
//
// `cycleAutoTasks.js` writes the English title and description AND
// `metadata.template = { id, params }`. The row renders the template in the
// reader's language; a task without one (older rows, parent-written tasks)
// renders as stored. The English catalog values equal the job's English
// sentences character for character, so an English reader sees no change.

type AutoParams = Record<string, unknown>;

function autoParams(task: Task): { id: string; params: AutoParams } | null {
  const tpl = task.metadata?.template;
  if (!tpl || typeof tpl.id !== 'string') return null;
  return { id: tpl.id, params: tpl.params ?? {} };
}

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

/**
 * A template speaks for the stored text only while that text is still the
 * job's own sentence. A parent can edit an auto task's title or description
 * (PATCH /api/tasks/:id keeps `metadata`), and their words must win over the
 * template — so the descriptor is used only when its ENGLISH rendering equals
 * what is stored.
 */
function stillGenerated(m: Msg | null, stored: string | undefined): Msg | null {
  if (!m) return null;
  return resolveMsg(i18n.getFixedT('en'), m) === (stored ?? '') ? m : null;
}

/** The auto-task title as a descriptor, or null when the task carries no known template or the title was edited. */
export function autoTaskTitleMsg(task: Task): Msg | null {
  return stillGenerated(templateTitle(task), task.title);
}

/**
 * The auto-task description as a descriptor, or null when the stored text is
 * what should render: no known template, text the family wrote themselves
 * (an appointment's own prep notes), or a description they edited.
 */
export function autoTaskDescriptionMsg(task: Task): Msg | null {
  return stillGenerated(templateDescription(task), task.description);
}

function templateTitle(task: Task): Msg | null {
  const tpl = autoParams(task);
  if (!tpl) return null;
  const p = tpl.params;
  switch (tpl.id) {
    case 'refill_prep':
      return str(p.dose)
        ? msg('tasks:auto.refill_prep.titleWithDose', { medication: str(p.medication), dose: str(p.dose) })
        : msg('tasks:auto.refill_prep.title', { medication: str(p.medication) });
    case 'post_chemo_recovery':
      return msg('tasks:auto.post_chemo_recovery.title', { cycle: p.cycle });
    case 'appointment_prep':
      return msg('tasks:auto.appointment_prep.title', { appointment: str(p.appointment) });
    default:
      return null;
  }
}

function templateDescription(task: Task): Msg | null {
  const tpl = autoParams(task);
  if (!tpl) return null;
  const p = tpl.params;
  switch (tpl.id) {
    case 'refill_prep': {
      // Four whole sentences rather than a "see record" fragment spliced in.
      const dose = str(p.dose);
      const doctor = str(p.doctor);
      if (doctor) {
        return dose
          ? msg('tasks:auto.refill_prep.descriptionDoctor', { doctor, dose })
          : msg('tasks:auto.refill_prep.descriptionDoctorNoDose', { doctor });
      }
      return dose
        ? msg('tasks:auto.refill_prep.descriptionPrescriber', { dose })
        : msg('tasks:auto.refill_prep.descriptionPrescriberNoDose');
    }
    case 'post_chemo_recovery':
      return msg('tasks:auto.post_chemo_recovery.description');
    case 'appointment_prep':
      return p.own_notes ? null : msg('tasks:auto.appointment_prep.description');
    default:
      return null;
  }
}

// ── Grouping by when (the list rewrite, 2026-09-10) ────────────────────────
//
// A parent scans a task list the way a task manager lays one out: by when the
// thing is due, not by which workflow state it is in. Status stays a FILTER
// (the popover's "Show" group) and a small word on the row ("In progress");
// the groups are time buckets — Todoist's Overdue / Today / Upcoming, Things'
// Today / Upcoming / Anytime. Completed tasks fall into one Done group at the
// end regardless of date, collapsed by default the way every task manager
// hides its logbook.

export type WhenKey = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'undated' | 'done';

export const WHEN_ORDER: WhenKey[] = ['overdue', 'today', 'tomorrow', 'week', 'later', 'undated', 'done'];

/** Which time bucket a task sits in, relative to `now`. */
export function whenKeyForTask(task: Task, now: Date = new Date()): WhenKey {
  if (task.status === 'completed') return 'done';
  if (!task.date_time) return 'undated';
  const at = new Date(task.date_time).getTime();
  if (!Number.isFinite(at)) return 'undated';
  const today = startOfDay(now).getTime();
  const tomorrow = addDays(startOfDay(now), 1).getTime();
  const dayAfter = addDays(startOfDay(now), 2).getTime();
  const weekEnd = addDays(startOfDay(now), 7).getTime();
  if (at < today) return 'overdue';
  if (at < tomorrow) return 'today';
  if (at < dayAfter) return 'tomorrow';
  if (at < weekEnd) return 'week';
  return 'later';
}

export interface WhenGroup {
  key: WhenKey;
  items: TaskSeries[];
}

/** The group heading's key (#2109 R3-B3: the group carries no baked label). */
export const WHEN_LABEL_KEY = {
  overdue:  'tasks:when.overdue',
  today:    'tasks:when.today',
  tomorrow: 'tasks:when.tomorrow',
  week:     'tasks:when.week',
  later:    'tasks:when.later',
  undated:  'tasks:when.undated',
  done:     'tasks:when.done',
} as const satisfies Record<WhenKey, string>;

const URGENCY_RANK: Record<Task['urgency'], number> = { critical: 0, high: 1, medium: 2, low: 3 };

function scheduledAt(t: Task): number {
  const at = t.date_time ? new Date(t.date_time).getTime() : Number.NaN;
  return Number.isFinite(at) ? at : Number.POSITIVE_INFINITY;
}

/**
 * Bucket a series list by when its representative is due, in `WHEN_ORDER`,
 * dropping empty buckets. Inside a bucket the soonest comes first and urgency
 * breaks ties; the Done bucket runs newest completion first, since that is the
 * one a parent scrolls back through.
 */
export function groupSeriesByWhen(series: TaskSeries[], now: Date = new Date()): WhenGroup[] {
  const buckets = new Map<WhenKey, TaskSeries[]>();
  for (const s of series) {
    const key = whenKeyForTask(s.representative, now);
    const list = buckets.get(key);
    if (list) list.push(s);
    else buckets.set(key, [s]);
  }
  const out: WhenGroup[] = [];
  for (const key of WHEN_ORDER) {
    const items = buckets.get(key);
    if (!items || items.length === 0) continue;
    if (key === 'done') {
      items.sort((a, b) => {
        const ad = Date.parse(a.representative.completion?.completed_at ?? '') || scheduledAt(a.representative);
        const bd = Date.parse(b.representative.completion?.completed_at ?? '') || scheduledAt(b.representative);
        return bd - ad;
      });
    } else {
      items.sort((a, b) => {
        // #2410: within one day an all-day task leads, even ahead of a task
        // set for exactly midnight. Its stored instant already sorts it
        // before every later time that day.
        const ar = a.representative;
        const br = b.representative;
        const aAll = isAllDayTask(ar);
        if (aAll !== isAllDayTask(br) && isSameDay(new Date(ar.date_time), new Date(br.date_time))) {
          return aAll ? -1 : 1;
        }
        const d = scheduledAt(ar) - scheduledAt(br);
        if (d !== 0 && Number.isFinite(d)) return d;
        const u = URGENCY_RANK[a.representative.urgency] - URGENCY_RANK[b.representative.urgency];
        if (u !== 0) return u;
        return a.representative.title.localeCompare(b.representative.title);
      });
    }
    out.push({ key, items });
  }
  return out;
}
